<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>Tunnels</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="customer_tunnels"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1],
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
          </v-toolbar>
        </template>
        <template v-slot:item.addresses="{ item }">
          {{ item.addresses.join(', ') }}
        </template>
        <template v-slot:item.static_routes="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="openStaticRoutesModal(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>View static routes</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="editItem(item)">mdi-pencil</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="500px">
      <template v-slot:activator="{ on }">
        <v-btn color="primary" dark class="mb-2" v-on="on">New Item</v-btn>
      </template>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="newForm" lazy-validation>
            <v-container>
              <v-row>
                <v-col v-if="editedIndex !== -1" cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.id"
                    label="ID"
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col v-if="editedIndex !== -1" cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.local"
                    label="Local"
                    disabled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.remote"
                    label="Remote"
                    :rules="[v => !!v || 'Remote is required']"
                  ></v-text-field>
                </v-col>
                <v-col v-if="editedIndex !== -1" cols="12" sm="12" md="12">
                  <v-textarea
                    v-model="formattedAddresses"
                    label="Addresses"
                    rows="2"
                    disabled
                  ></v-textarea>
                </v-col>
                <v-col cols="12" sm="12" md="12">
                  <v-select
                    v-model="editedItem.kind"
                    :items="['gre', 'ipip', 'ip6tnl']"
                    label="Kind"
                    :rules="[v => !!v || 'Kind is required']"
                    :disabled="editedIndex !== -1"
                  />
                </v-col>
                <v-col v-if="editedIndex === -1" cols="12" sm="12" md="12">
                  <v-checkbox
                    v-model="editedItem.use_v4_overlay"
                    label="Use IPv4 Overlay"
                    rows="2"
                    hint="Whether or not to assign an IPv4 tunnel overlay address"
                    persistent-hint
                  ></v-checkbox>
                </v-col>
                <v-col v-if="editedIndex === -1" cols="12" sm="12" md="12">
                  <v-checkbox
                    v-model="editedItem.use_v6_overlay"
                    label="Use IPv6 Overlay"
                    rows="2"
                    hint="Whether or not to assign an IPv6 tunnel overlay address. Only use this if you'll be establishing the BGP session over an inner IPv6 address"
                    persistent-hint
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="12" md="12">
                  <v-text-field
                    v-model="editedItem.user_comment"
                    label="Comment"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="staticRoutesModal" max-width="760px">
      <v-card>
        <v-card-title>
          <span class="headline">Static Routes</span>
        </v-card-title>
        <v-card-subtitle>
          View static routes for the selected tunnel. A prefix may be routed over multiple tunnels, in which case ECMP routing will occur between them
        </v-card-subtitle>
        <v-card-text>
          <div v-if="selectedTunnelIdForStaticRoute && isStaticRoutesLoading(selectedTunnelIdForStaticRoute)" class="d-flex align-center">
            <v-progress-circular indeterminate size="18" width="2" class="mr-2" />
            Loading static routes...
          </div>
          <v-simple-table v-else-if="selectedTunnelIdForStaticRoute && getStaticRoutes(selectedTunnelIdForStaticRoute).length > 0" dense>
            <thead>
              <tr>
                <th>Network</th>
                <th>
                  <span>Nexthop</span>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small class="ml-1" v-bind="attrs" v-on="on">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      The next hop to forward traffic over
                    </span>
                  </v-tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="route in getStaticRoutes(selectedTunnelIdForStaticRoute)" :key="route.network">
                <td>{{ route.network }}</td>
                <td>{{ route.nexthop }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeStaticRoutesModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";

export default {
  mounted() {
    this.loadData();
  },
  data: () => ({
    selected: [],
    headers: [
      { text: "ID", align: "left", sortable: false, value: "id" },
      { text: "Local", value: "local" },
      { text: "Remote", value: "remote" },
      { text: "Addresses", value: "addresses" },
      { text: "Kind", value: "kind" },
      { text: "Comment", value: "user_comment" },
      { text: "Static Routes", value: "static_routes", sortable: false },
      { text: "Actions", value: "actions", sortable: false },
    ],
    customer_tunnels: [],
    staticRoutesByTunnelId: {},
    staticRoutesLoadingByTunnelId: {},
    errors: [],
    loading: true,
    dialog: false,
    editedIndex: -1,
    editedItem: {
      id: "",
      local: "",
      remote: "",
      kind: "",
      addresses: [],
      use_v4_overlay: false,
      use_v6_overlay: false,
      user_comment: "",
    },
    defaultItem: {
      id: "",
      local: "",
      remote: "",
      kind: "",
      addresses: [],
      use_v4_overlay: false,
      use_v6_overlay: false,
      user_comment: "",
    },
    search: "",
    staticRoutesModal: false,
    selectedTunnelIdForStaticRoute: null,
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Item" : "Edit Item";
    },
    formattedAddresses: {
      get() {
        return this.editedItem.addresses.join("\n");
      },
      set(value) {
        this.editedItem.addresses = value.split("\n");
      },
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
  },
  methods: {
    loadData() {
      api
        .query("customer_tunnels")
        .then((response) => {
          this.customer_tunnels = response.data.customer_tunnels;
        })
        .catch((error) => {
          this.errors.push(...Errors.extractErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editItem(item) {
      this.editedIndex = this.customer_tunnels.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },
    save() {
      if (!this.$refs.newForm.validate()) return;

      if (this.editedIndex > -1) {
        api
          .patch("customer_tunnels/" + this.editedItem.id, {
            user_comment: this.editedItem.user_comment,
            remote: this.editedItem.remote,
          })
          .then((response) => {
            Object.assign(
              this.customer_tunnels.find((val) => val.id == response.data.id),
              response.data
            );
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      } else {
        api
          .post("customer_tunnels", {
            remote: this.editedItem.remote,
            kind: this.editedItem.kind,
            use_v4_overlay: this.editedItem.use_v4_overlay,
            use_v6_overlay: this.editedItem.use_v6_overlay,
            user_comment: this.editedItem.user_comment,
          })
          .then((response) => {
            this.customer_tunnels.push(response.data);
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      }
      this.close();
    },
    openStaticRoutesModal(item) {
      this.selectedTunnelIdForStaticRoute = item.id;
      this.staticRoutesModal = true;
      this.loadTunnelStaticRoutes(item.id);
    },
    closeStaticRoutesModal() {
      this.staticRoutesModal = false;
      this.selectedTunnelIdForStaticRoute = null;
    },
    isStaticRoutesLoading(tunnelId) {
      return !!this.staticRoutesLoadingByTunnelId[tunnelId];
    },
    getStaticRoutes(tunnelId) {
      return this.staticRoutesByTunnelId[tunnelId] || [];
    },
    loadTunnelStaticRoutes(tunnelId) {
      this.$set(this.staticRoutesLoadingByTunnelId, tunnelId, true);

      api
        .query(`customer_tunnels/${tunnelId}/static_routes`)
        .then((response) => {
          this.$set(
            this.staticRoutesByTunnelId,
            tunnelId,
            response.data.tunnel_static_routes || []
          );
        })
        .catch((error) => {
          this.errors.push(...Errors.extractErrorMessage(error));
        })
        .finally(() => {
          this.$set(this.staticRoutesLoadingByTunnelId, tunnelId, false);
        });
    },
  },
};
</script>
